import BigNumber from 'bignumber.js'
import {
  AdventureUserReward,
  AdventureUserRewardResponse,
  InfoReward,
  UserWager,
  WagerAdventure,
} from 'config/types/bonus/WagerAdventure'
import useModal from 'hooks/useModal'
import { useRequest } from 'hooks/useRequest'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import WagerAdventureService from 'services/WagerAdventureService'
import { usePlayingGame, useTiers, useWagerAdventure } from 'state/app/hooks'
import { useAuth, useUserInfo } from 'state/auth/hooks'
import { useProfileTier } from 'state/profile/hooks'
import { Game } from 'config/types/game'
import GameService from 'services/GameService'
import { useRouter } from 'hooks/useRouter'
import { RouteConfig } from 'config/constants/route'
import { useAppDispatch } from 'state'
import { updateWagerAdventure } from 'state/app/actions'
import RewardAdventureBonusModal from './components/RewardAdventureBonusModal'

export const getRewardByUserLevel = (rewards: InfoReward[], userLevel: number) => {
  return rewards[userLevel - 1] || null
}

export const getProgressReward = (rewards: InfoReward[], level: number, wagerUsd: string | number) => {
  const currentReward = getRewardByUserLevel(rewards, level)
  const nextReward = rewards[level]
  const nextWager = new BigNumber(nextReward?.wagerUsd)
  const currentWager = new BigNumber(currentReward?.wagerUsd)
  const isMaxLevel = level === rewards.length

  const userWager = new BigNumber(wagerUsd).minus(currentWager)

  const percent = isMaxLevel
    ? 100
    : userWager.dividedBy(nextWager.minus(currentWager)).multipliedBy(100).toNumber() || 0

  return Math.floor(Math.min(Math.max(percent, 0), 100) * 100) / 100
}

export const sortUsersByWager = (users: AdventureUserReward[]): AdventureUserReward[] => {
  return users.sort((a, b) => (b?.wagerUsd || 0) - (a?.wagerUsd || 0))
}

export const useFetchWagerAdventure = () => {
  const { hasSession } = useAuth()
  const router = useRouter()
  const { execute, cancelAllRequest } = useRequest()
  const wagerAdventure = useWagerAdventure()

  const [listUserReward, setListUserReward] = useState<AdventureUserReward[]>([])
  const tiers = useTiers()
  const { displayName, userCode, avatar } = useUserInfo()
  const userTier = useProfileTier()
  const [onPresent] = useModal(RewardAdventureBonusModal)
  const dispatch = useAppDispatch()

  const userProfile = useMemo(
    () => ({
      displayName,
      userCode,
      avatar,
      tier: userTier,
    }),
    [displayName, userCode, avatar, userTier],
  )

  const [userWager, setUserWager] = useState<UserWager>({
    level: null,
    wagerUsd: null,
    reward: null,
    nextReward: null,
    code: null,
    rank: null,
  })

  const [progressReward, setProgressReward] = useState<number>(0)
  const { playingGame } = usePlayingGame()

  const onPresentRewardTopModal = async (adventure: WagerAdventure, user: UserWager) => {
    if (router.pathname !== RouteConfig.WagerAdventure) return

    if (!hasSession || !user?.reward?.bonus || !user?.rank || playingGame) {
      return
    }

    if (user.rank > adventure.rewardTopPlayers.length) {
      return
    }

    const adventureId = adventure?.id?.toString()
    const storeAdventure = JSON.parse(localStorage.getItem('wager-adventure') || '{}')
    const users = storeAdventure?.[adventureId]?.users || []

    const isWatched = users.find((user) => user === userCode)

    if (adventureId && userCode && !isWatched) {
      localStorage.setItem(
        'wager-adventure',
        JSON.stringify({
          [adventureId]: {
            id: adventureId,
            users: [...users, userCode],
          },
        }),
      )
      if (adventure?.rewardTopPlayers?.[user.rank - 1]?.bonus && user.rank) {
        onPresent({
          bonus: adventure.rewardTopPlayers?.[user.rank - 1]?.bonus,
          rank: user.rank,
        })
      }
    }
  }

  const fetch = useCallback(async () => {
    await cancelAllRequest()

    let progress = 0
    let userData: UserWager = {
      level: null,
      wagerUsd: null,
      reward: null,
      nextReward: null,
      code: null,
      rank: null,
    }

    const rewards = wagerAdventure?.rewardByLevels
    const listUserReward = (await execute(WagerAdventureService.getListUserReward(wagerAdventure.id)))?.data || []

    // const TIME_ENABLE_DISPLAY_LEVLE = 60 * 60 * 24 * 14 * 1000 // 2 weeks
    // const isEnableDisplayLevel = wagerAdventure.startDate + TIME_ENABLE_DISPLAY_LEVLE < Date.now()

    const users = listUserReward.map((user: AdventureUserRewardResponse) => {
      const reward = getRewardByUserLevel(rewards, user.level)
      const tier = tiers.find((tier) => tier.id === user.tierId)
      const isHidden = !user.displayName
      const isUserProfile = hasSession && isHidden && user.code === userProfile?.userCode

      const data: AdventureUserReward = {
        avatar: user.avatar,
        displayName: isUserProfile ? userProfile?.displayName : user.displayName,
        rank: user.rank,
        tier,
        level: user.level,
        wagerUsd: user.wagerUsd,
        code: user?.code,

        bonus: reward?.bonus,
        enableDisplayLevel: true,
      }

      return data
    })

    if (hasSession && userCode) {
      const userStats = (await execute(WagerAdventureService.getUserStats(wagerAdventure.id)))?.data

      if (userStats) {
        const { level, wagerUsd: userWagerUsd, code } = userStats

        // rewards started from index 1 not 0
        const isMaxLevel = level >= rewards.length
        const userReward = rewards[isMaxLevel ? level - 1 : level]
        const ranksUserRewards = sortUsersByWager(users)
        const userRank = ranksUserRewards.findIndex((user) => user && user.displayName === displayName) + 1

        progress = getProgressReward(rewards, level, userWagerUsd)
        const isMaxReward = !userWager?.nextReward && progress === 100

        userData = {
          level,
          wagerUsd: userWagerUsd,
          reward: isMaxReward
            ? {
                ...userReward,
                bonusName: 'Completed',
              }
            : userReward,
          nextReward: rewards[level + 1],
          code,
          rank: userRank,
        }

        if (wagerAdventure.endDate < Date.now()) {
          onPresentRewardTopModal(wagerAdventure, userData)
        }
      }
    }

    setUserWager(userData)
    setProgressReward(progress)

    setListUserReward(users)

    return {
      userWager: userData,
      wagerAdventure,
    }
  }, [userCode, hasSession, wagerAdventure])

  const fetchWagerAdventure = async () => {
    const response = await WagerAdventureService.getAdventure()

    if (response?.data?.id) {
      dispatch(
        updateWagerAdventure({
          wagerAdventure: response.data,
        }),
      )
    }
  }

  useEffect(() => {
    if (wagerAdventure) {
      fetch()
    } else if (!wagerAdventure?.id) {
      fetchWagerAdventure()
    }
  }, [wagerAdventure, userCode, hasSession])

  const callbackCountdown = useCallback(async () => {
    fetch()
  }, [fetch])

  return useMemo(
    () => ({
      isMaxReward: !userWager?.nextReward && progressReward === 100,
      isExpired: wagerAdventure && wagerAdventure.endDate < Date.now(),
      listUserReward,
      wagerAdventure,
      userWager,
      progressReward,
      onUpdateProgress: setProgressReward,
      refetch: fetch,
      onPresentRewardModal: callbackCountdown,
    }),
    [listUserReward, wagerAdventure, userWager, progressReward, fetch, setProgressReward, hasSession],
  )
}
export const useIsWagerAdventure = (gameCollectionId: number, game: Game) => {
  const { execute } = useRequest()
  const [acceptGame, setAcceptGame] = useState<boolean>(false)
  const refValidateGame = useRef(null)
  const { isSigned } = useAuth()

  useEffect(() => {
    const fetch = async () => {
      const data = await execute(
        GameService.getGames({
          slug: game.slug,
          gameCollectionId,
        }),
      )

      refValidateGame.current = game.code
      setAcceptGame(data?.data?.items?.length > 0)
    }

    if (gameCollectionId && game?.code && refValidateGame.current !== game.code && isSigned) {
      fetch()
    }
  }, [gameCollectionId, game, isSigned])

  return useMemo(() => acceptGame, [acceptGame])
}

